import httpService from "../../services/httpService";
import { ApiEndpoints } from "../../constants/apiEndpoints";
import { PaginationMeta } from "../../types/api";
import {
  AdminNotification,
  BulkNotificationSummary,
  NotificationAction,
  NotificationAudience,
  NotificationCampaign,
  NotificationSendSummary,
  NotificationType,
  PushStatus,
} from "../../types/admin";

export interface NotificationListParams {
  page?: number;
  limit?: number;
  push_status?: PushStatus;
  user_id?: string;
}

export interface SendBulkNotificationPayload {
  title: string;
  body: string;
  data?: Record<string, unknown>;
  audience: NotificationAudience;
}

export interface SendSingleNotificationPayload {
  user_id: string;
  title: string;
  body: string;
  data?: Record<string, unknown>;
}

export interface NotificationListResponse {
  items: AdminNotification[];
  meta: PaginationMeta | null;
}

export interface NotificationCampaignListResponse {
  items: NotificationCampaign[];
  meta: PaginationMeta | null;
}

function normalizeData(data?: Record<string, unknown>): Record<string, string> | undefined {
  if (!data) return undefined;
  const normalized: Record<string, string> = {};
  Object.entries(data).forEach(([key, value]) => {
    normalized[key] = String(value);
  });
  return normalized;
}

function asRecord(value: unknown): Record<string, any> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, any>)
    : {};
}

function firstValue(records: Record<string, any>[], keys: string[]) {
  for (const record of records) {
    for (const key of keys) {
      if (record[key] !== undefined && record[key] !== null) return record[key];
    }
  }
  return undefined;
}

function normalizePushStatus(value: unknown): PushStatus | undefined {
  const status = String(value || "").toLowerCase();
  return ["pending", "sent", "failed", "skipped"].includes(status)
    ? (status as PushStatus)
    : undefined;
}

function normalizeSendSummary(
  payload: unknown,
  requestedUsersFallback: number
): NotificationSendSummary {
  const root = asRecord(payload);
  const records = [
    asRecord(root.push_result),
    asRecord(root.push),
    asRecord(root.delivery),
    asRecord(root.delivery_result),
    asRecord(root.summary),
    asRecord(root.notification),
    asRecord(root.result),
    root,
  ];

  const numberFrom = (keys: string[], fallback = 0) => {
    const value = firstValue(records, keys);
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };
  const reasonValue = firstValue(records, ["reason", "push_reason", "message"]);
  let pushStatus: PushStatus | undefined;
  for (const record of records) {
    pushStatus = normalizePushStatus(
      firstValue([record], ["push_status", "delivery_status", "status"])
    );
    if (pushStatus) break;
  }

  return {
    requested_users: numberFrom(
      ["requested_users", "requested", "requested_count"],
      requestedUsersFallback
    ),
    targeted_devices: numberFrom(
      ["targeted_devices", "targeted", "device_count", "target_count"]
    ),
    sent: numberFrom(["sent", "sent_count", "success", "success_count"]),
    failed: numberFrom(["failed", "failed_count", "failure_count"]),
    skipped: numberFrom(["skipped", "skipped_count"]),
    push_status: pushStatus,
    reason: typeof reasonValue === "string" && reasonValue.trim()
      ? reasonValue
      : null,
  };
}

export async function sendNotification(
  payload: SendBulkNotificationPayload
): Promise<BulkNotificationSummary> {
  const response = await httpService.post<any>(ApiEndpoints.ADMIN_NOTIFICATIONS_SEND, {
    ...payload,
    data: normalizeData(payload.data),
  });

  const summary = normalizeSendSummary(response.data, 0);
  const root = asRecord(response.data);
  const campaign =
    root.campaign ||
    asRecord(root.result).campaign ||
    asRecord(root.data).campaign ||
    {};

  return { ...summary, campaign: normalizeCampaign(campaign) };
}

export async function sendSingleNotification(
  payload: SendSingleNotificationPayload
): Promise<NotificationSendSummary> {
  const response = await httpService.post<any>(ApiEndpoints.ADMIN_NOTIFICATIONS_SEND_SINGLE, {
    ...payload,
    data: normalizeData(payload.data),
  });

  return normalizeSendSummary(response.data, 1);
}

export async function fetchNotifications(
  params: NotificationListParams = {}
): Promise<NotificationListResponse> {
  const response = await httpService.get<any>(ApiEndpoints.ADMIN_NOTIFICATIONS, {
    params,
  });

  const rows = Array.isArray(response.data)
    ? response.data
    : Array.isArray((response.data as any)?.items)
      ? (response.data as any).items
      : Array.isArray((response.data as any)?.notifications)
        ? (response.data as any).notifications
      : [];

  return {
    items: rows.map(normalizeNotification),
    meta: response.meta || null,
  };
}

export async function fetchNotificationDetail(
  notificationId: string
): Promise<AdminNotification> {
  const response = await httpService.get<any>(`${ApiEndpoints.ADMIN_NOTIFICATIONS_BY_ID}/${notificationId}`);
  return normalizeNotification(response.data, notificationId);
}

export async function fetchNotificationCampaigns(
  page = 1,
  limit = 20
): Promise<NotificationCampaignListResponse> {
  const response = await httpService.get<any>(ApiEndpoints.ADMIN_NOTIFICATION_CAMPAIGNS, {
    params: { page, limit },
  });
  const rows = Array.isArray(response.data)
    ? response.data
    : Array.isArray((response.data as any)?.items)
      ? (response.data as any).items
      : Array.isArray((response.data as any)?.campaigns)
        ? (response.data as any).campaigns
        : [];
  return { items: rows.map(normalizeCampaign), meta: response.meta || null };
}

function normalizeNotification(item: any, fallbackId = ""): AdminNotification {
  return {
    id: String(item?.id || item?.notification_id || item?._id || fallbackId),
    user_id: String(item?.user_id || ""),
    type: String(item?.type || "USER_REGISTERED") as NotificationType,
    title: String(item?.title || ""),
    body: String(item?.body || ""),
    action: String(item?.action || "NONE") as NotificationAction,
    entity_type: item?.entity_type == null ? null : String(item.entity_type),
    entity_id: item?.entity_id == null ? null : String(item.entity_id),
    is_read: Boolean(item?.is_read),
    push_status: String(item?.push_status || item?.status || "pending") as PushStatus,
    created_at: String(item?.created_at || ""),
    updated_at: String(item?.updated_at || ""),
    read_at: item?.read_at == null ? null : String(item.read_at),
    sent_at: item?.sent_at == null ? null : String(item.sent_at),
  };
}

function normalizeCampaign(item: any): NotificationCampaign {
  const rawAudience = item?.audience;
  const audienceType =
    typeof rawAudience === "object" && rawAudience?.type === "users"
      ? "users"
      : rawAudience === "users"
        ? "users"
        : "all";
  const rawData = item?.data && typeof item.data === "object" ? item.data : {};
  return {
    campaign_id: String(item?.campaign_id || item?._id || ""),
    title: String(item?.title || ""),
    body: String(item?.body || ""),
    audience: {
      type: audienceType,
      user_ids: Array.isArray(rawAudience?.user_ids)
        ? rawAudience.user_ids.map(String)
        : [],
    },
    data: Object.fromEntries(
      Object.entries(rawData).map(([key, value]) => [key, String(value)])
    ),
    requested_users: Number(item?.requested_users || 0),
    targeted_devices: Number(item?.targeted_devices || 0),
    sent: Number(item?.sent || 0),
    failed: Number(item?.failed || 0),
    skipped: Number(item?.skipped || 0),
    created_at: String(item?.created_at || ""),
    updated_at: String(item?.updated_at || ""),
  };
}

// Legacy compatibility wrappers
export async function startCampaign(payload: {
  title: string;
  body: string;
  data?: Record<string, any>;
  dryRun?: boolean;
  target?: {
    type: "all" | "user" | "segment";
    value?: string | null;
    language?: "english" | "turkish";
  };
}) {
  const audience = payload.target?.type === "all" ? "all" : "users";
  const response = await sendNotification({
    title: payload.title,
    body: payload.body,
    data: payload.data,
    audience: {
      type: audience,
      user_ids: payload.target?.value ? [String(payload.target.value)] : [],
    },
  });
  return { id: undefined, ...response };
}

export async function getCampaignStatus(_campaignId: string) {
  const campaigns = await fetchNotificationCampaigns();
  return campaigns.items;
}
